import { CurrencyAmount, JSBI, Token, Trade, Pair } from '@uniswap/sdk'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ArrowDown } from 'react-feather'
import ReactGA from 'react-ga'
import { Button, Text } from 'rebass'
import styled, { ThemeContext } from 'styled-components'
import AddressInputPanel from '../../components/AddressInputPanel'
import { ButtonError, ButtonLight, ButtonPrimary, ButtonConfirmed } from '../../components/Button'
import Card, { GreyCard } from '../../components/Card'
import Column, { AutoColumn } from '../../components/Column'
import ConfirmSwapModal from '../../components/swap/ConfirmSwapModal'
import DepositModal from '../../components/PoolModal/DepositModal'
import CreatePoolModal from '../../components/PoolModal/CreatePoolModal'
import CurrencyInputPanel from '../../components/CurrencyInputPanel'
import { SwapPoolTabs } from '../../components/NavigationTabs'
import { AutoRow, RowBetween } from '../../components/Row'
import AdvancedSwapDetailsDropdown from '../../components/swap/AdvancedSwapDetailsDropdown'
import confirmPriceImpactWithoutFee from '../../components/swap/confirmPriceImpactWithoutFee'
import { ArrowWrapper, BottomGrouping, SwapCallbackError, Wrapper } from '../../components/swap/styleds'
import TradePrice from '../../components/swap/TradePrice'
import TokenWarningModal from '../../components/TokenWarningModal'
import ProgressSteps from '../../components/ProgressSteps'

import { ChainN, HexChainId, INITIAL_ALLOWED_SLIPPAGE } from '../../constants'
import { useActiveWeb3React } from '../../hooks'
import { useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallbackFromTrade } from '../../hooks/useApproveCallback'
import useENSAddress from '../../hooks/useENSAddress'
import { useSwapCallback } from '../../hooks/useSwapCallback'
import useToggledVersion, { Version } from '../../hooks/useToggledVersion'
import useWrapCallback, { WrapType } from '../../hooks/useWrapCallback'
import { useToggleSettingsMenu, useWalletModalToggle } from '../../state/application/hooks'
import { usePairs } from '../../data/Reserves'

import { Field } from '../../state/swap/actions'
import {
    useDefaultsFromURLSearch,
    useDerivedSwapInfo,
    useSwapActionHandlers,
    useSwapState
} from '../../state/swap/hooks'
import { toV2LiquidityToken, useTrackedTokenPairs, useExpertModeManager, useUserSlippageTolerance } from '../../state/user/hooks'
import { LinkStyledButton, TYPE } from '../../theme'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import { computeTradePriceBreakdown, warningSeverity } from '../../utils/prices'
import AppBody from '../AppBody'
import { ClickableText } from '../Pool/styleds'
import Loader from '../../components/Loader'
import { Box, Grid, useMediaQuery, createMuiTheme } from '@material-ui/core'
import Copy from '../../components/AccountDetails/Copy'
import Settings from '../../components/Settings'
import { useDarkModeManager } from '../../state/user/hooks'
import { ReactComponent as SwapSvg } from '../../assets/images/swap.svg'
import USDT from '../../assets/images/USDT.png'

import Header from '../../components/Header'
// import './swap.css';
import Footer from '../../components/Footer'
import './pools.css';
import FullPositionCard from '../../components/PositionCard'
import { useTokenBalancesWithLoadingIndicator } from '../../state/wallet/hooks'
import { unwrappedToken } from '../../utils/wrappedCurrency'


const EmptyProposals = styled.div`
  border: 1px solid ${({ theme }) => theme.text4};
  padding: 16px 12px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

export default function Swap() {
    const isMobile = useMediaQuery('(max-width:1150px)')
    const [darkMode, toggleDarkMode] = useDarkModeManager()

    const loadedUrlParams = useDefaultsFromURLSearch()
    const [email, setEmail] = useState('')
    // token warning stuff
    const [loadedInputCurrency, loadedOutputCurrency] = [
        useCurrency(loadedUrlParams?.inputCurrencyId),
        useCurrency(loadedUrlParams?.outputCurrencyId)
    ]
    const [dismissTokenWarning, setDismissTokenWarning] = useState<boolean>(false)
    const urlLoadedTokens: Token[] = useMemo(
        () => [loadedInputCurrency, loadedOutputCurrency]?.filter((c): c is Token => c instanceof Token) ?? [],
        [loadedInputCurrency, loadedOutputCurrency]
    )
    const handleConfirmTokenWarning = useCallback(() => {
        setDismissTokenWarning(true)
    }, [])

    const { account } = useActiveWeb3React()
    const theme = useContext(ThemeContext)
    const { ethereum } = window

    // toggle wallet when disconnected
    const toggleWalletModal = useWalletModalToggle()

    // for expert mode
    const toggleSettings = useToggleSettingsMenu()
    const [isExpertMode] = useExpertModeManager()

    // get custom setting values for user
    const [allowedSlippage] = useUserSlippageTolerance()

    // swap state
    const { independentField, typedValue, recipient } = useSwapState()
    const {
        v1Trade,
        v2Trade,
        currencyBalances,
        parsedAmount,
        currencies,
        inputError: swapInputError
    } = useDerivedSwapInfo()
    const { wrapType, execute: onWrap, inputError: wrapInputError } = useWrapCallback(
        currencies[Field.INPUT],
        currencies[Field.OUTPUT],
        typedValue
    )

    const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE
    const { address: recipientAddress } = useENSAddress(recipient)
    const toggledVersion = useToggledVersion()
    const tradesByVersion = {
        [Version.v1]: v1Trade,
        [Version.v2]: v2Trade
    }
    const trade = showWrap ? undefined : tradesByVersion[toggledVersion]

    const parsedAmounts = showWrap
        ? {
            [Field.INPUT]: parsedAmount,
            [Field.OUTPUT]: parsedAmount
        }
        : {
            [Field.INPUT]: independentField === Field.INPUT ? parsedAmount : trade?.inputAmount,
            [Field.OUTPUT]: independentField === Field.OUTPUT ? parsedAmount : trade?.outputAmount
        }

    const { onSwitchTokens, onCurrencySelection, onUserInput, onChangeRecipient } = useSwapActionHandlers()
    const isValid = !swapInputError
    const dependentField: Field = independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT

    const handleTypeInput = useCallback(
        (value: string) => {
            onUserInput(Field.INPUT, value)
        },
        [onUserInput]
    )
    const handleTypeOutput = useCallback(
        (value: string) => {
            onUserInput(Field.OUTPUT, value)
        },
        [onUserInput]
    )

    // modal and loading
    const [{ showConfirm, tradeToConfirm, swapErrorMessage, attemptingTxn, txHash }, setSwapState] = useState<{
        showConfirm: boolean
        tradeToConfirm: Trade | undefined
        attemptingTxn: boolean
        swapErrorMessage: string | undefined
        txHash: string | undefined
    }>({
        showConfirm: false,
        tradeToConfirm: undefined,
        attemptingTxn: false,
        swapErrorMessage: undefined,
        txHash: undefined
    })

    const formattedAmounts = {
        [independentField]: typedValue,
        [dependentField]: showWrap
            ? parsedAmounts[independentField]?.toExact() ?? ''
            : parsedAmounts[dependentField]?.toSignificant(6) ?? ''
    }

    const route = trade?.route
    const userHasSpecifiedInputOutput = Boolean(
        currencies[Field.INPUT] && currencies[Field.OUTPUT] && parsedAmounts[independentField]?.greaterThan(JSBI.BigInt(0))
    )
    const noRoute = !route

    // check whether the user has approved the router on the input token
    const [approval, approveCallback] = useApproveCallbackFromTrade(trade, allowedSlippage)

    // check if user has gone through approval process, used to show two step buttons, reset on token change
    const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)

    // mark when a user has submitted an approval, reset onTokenSelection for input field
    useEffect(() => {
        if (approval === ApprovalState.PENDING) {
            setApprovalSubmitted(true)
        }
    }, [approval, approvalSubmitted])

    const maxAmountInput: CurrencyAmount | undefined = maxAmountSpend(currencyBalances[Field.INPUT])
    const atMaxAmountInput = Boolean(maxAmountInput && parsedAmounts[Field.INPUT]?.equalTo(maxAmountInput))
    const { priceImpactWithoutFee } = computeTradePriceBreakdown(trade)
    // warnings on slippage
    const priceImpactSeverity = warningSeverity(priceImpactWithoutFee)

    // show approve flow when: no error on inputs, not approved or pending, or approved in current session
    // never show if price impact is above threshold in non expert mode
    const showApproveFlow =
        !swapInputError &&
        (approval === ApprovalState.NOT_APPROVED ||
            approval === ApprovalState.PENDING ||
            (approvalSubmitted && approval === ApprovalState.APPROVED)) &&
        !(priceImpactSeverity > 3 && !isExpertMode)

    const handleConfirmDismiss = useCallback(() => {
        setSwapState({ showConfirm: false, tradeToConfirm, attemptingTxn, swapErrorMessage, txHash })
        // if there was a tx hash, we want to clear the input
        if (txHash) {
            onUserInput(Field.INPUT, '')
        }
    }, [attemptingTxn, onUserInput, swapErrorMessage, tradeToConfirm, txHash])

    const handleAcceptChanges = useCallback(() => {
        setSwapState({ tradeToConfirm: trade, swapErrorMessage, txHash, attemptingTxn, showConfirm })
    }, [attemptingTxn, showConfirm, swapErrorMessage, trade, txHash])

    const handleInputSelect = useCallback(
        inputCurrency => {
            setApprovalSubmitted(false) // reset 2 step UI for approvals
            onCurrencySelection(Field.INPUT, inputCurrency)
        },
        [onCurrencySelection]
    )

    const handleMaxInput = useCallback(() => {
        maxAmountInput && onUserInput(Field.INPUT, maxAmountInput.toExact())
    }, [maxAmountInput, onUserInput])

    const handleOutputSelect = useCallback(outputCurrency => onCurrencySelection(Field.OUTPUT, outputCurrency), [
        onCurrencySelection
    ])

    const addMaticToMetamask = () => {
        if (ethereum) {
            // @ts-ignore
            ethereum?.request({
                method: 'wallet_switchEthereumChain',
                params: [
                    {
                        chainId: HexChainId
                    }
                ]
            })
                .then((result: any) => { })
                .catch((error: any) => {
                    console.log('wallet_switchEthereumChain error', error)
                    // @ts-ignore
                    ethereum.request({
                        method: 'wallet_addEthereumChain',
                        params: [{
                            chainId: HexChainId,
                            chainName: 'APE',
                            nativeCurrency: {
                                name: 'APE',
                                symbol: 'APE',
                                decimals: 18
                            },
                            rpcUrls: ['https://base-sepolia.blockpi.network/v1/rpc/public'],
                            blockExplorerUrls: ['https://sepolia.basescan.org']
                        }]
                    })
                        .then((result: any) => { })
                        .catch((error: any) => {
                            if (error.code === 4001) {
                                // EIP-1193 userRejectedRequest error
                                console.log('We can encrypt anything without the key.')
                            } else {
                                console.error(error)
                            }
                        })
                })
        }
    }

    const onSubmit = async (e: any) => {
        e.preventDefault()
        const url = `https://codl.us10.list-manage.com/subscribe/post`

        const formdata = new FormData()
        formdata.append('u', 'ff9cd4d6a49271f38595404c7')
        formdata.append('id', '5220a127ca')
        formdata.append('f_id', '004fbbe5f0')
        formdata.append('MERGE0', email)

        fetch(url, { method: 'POST', body: formdata, mode: 'no-cors' })
            .then(response => {
                alert('Success')
            })
            .catch(error => {
                alert('Error')
                console.log(error)
            })
    }

    const theme1 = createMuiTheme()

    // Use useMediaQuery hook to check screen size
    const isSmallScreen = useMediaQuery(theme1.breakpoints.down('sm'))
    const isMediumScreen = useMediaQuery(theme1.breakpoints.between('sm', 'lg'))

    const isLargeScreen = useMediaQuery(theme1.breakpoints.up('xl'))
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const handleDismissCreate = useCallback(() => {
        setCreateModalOpen(false)
    }, [setCreateModalOpen])

    // fetch the user's balances of all tracked V2 LP tokens
    const trackedTokenPairs = useTrackedTokenPairs()
    const tokenPairsWithLiquidityTokens = useMemo(
        () => trackedTokenPairs.map((tokens: any) => ({ liquidityToken: toV2LiquidityToken(tokens), tokens })),
        [trackedTokenPairs]
    )
    const liquidityTokens = useMemo(() => tokenPairsWithLiquidityTokens.map((tpwlt: any) => tpwlt.liquidityToken), [
        tokenPairsWithLiquidityTokens
    ])
    const [v2PairsBalances, fetchingV2PairBalances] = useTokenBalancesWithLoadingIndicator(
        account ?? undefined,
        liquidityTokens
    )

    // fetch the reserves for all V2 pools in which the user has a balance
    const liquidityTokensWithBalances = useMemo(
        () =>
            tokenPairsWithLiquidityTokens.filter(({ liquidityToken }) =>
                v2PairsBalances[liquidityToken.address]?.greaterThan('0')
            ),
        [tokenPairsWithLiquidityTokens, v2PairsBalances]
    )
    console.log("liquidityTokensWithBalances", liquidityTokensWithBalances)

    const v2Pairs = usePairs(liquidityTokensWithBalances.map(({ tokens }) => tokens))
    // const v2IsLoading = fetchingV2PairBalances || v2Pairs?.length < liquidityTokensWithBalances.length || v2Pairs?.some((V2Pair: any) => !V2Pair)
    const allV2PairsWithLiquidity = v2Pairs.map(([, pair]: any) => pair).filter((v2Pair: any): v2Pair is Pair => Boolean(v2Pair))

    return (
        <>
            <div className="w-full flex flex-col h-full min-h-screen bg-[#1b1b1b] bg-contain bg-center bg-no-repeat bg-base">
                <div className="">
                    <Header />
                </div>

                <div className="w-full overflow-auto msm:pt-10 md:pt-0 md:flex md:flex-1 md:flex-col md:items-center md:justify-center bg-mobile bg-contain bg-top bg-no-repeat md:bg-none h-full pb-0 bg-base" style={{ backgroundSize: '100%' }}>
                   
                    <div className="flex items-center w-full text-lg text-white bg-top md:bg-center bg-no-repeat bg-[length:75%]">
                        <div className="px-[2.5rem] py-[2rem] w-[55rem] mx-auto border border-white bg-[#333] rounded-[10px] mb-[90px]">
                            <div className="w-full flex justify-end">
                                <a>
                                    <ButtonLight onClick={() => setCreateModalOpen(true)}>
                                        Create Liquidity
                                    </ButtonLight>
                                </a>
                            </div>

                            <div className="h-full py-[12.5vh] space-y-8 pb-[10vh]">
                                {/* <div className="border-[2px] rounded-xl md:rounded-none border-[#333333]">
                                    <div className="bg-[#333333] flex flex-col md:flex-row px-4 md:px-7 py-[16px] md:py-[1.675rem] md:items-center">
                                        <div className="flex items-center">
                                            <div className="flex items-center -space-x-3 w-[8rem]">
                                                <div className="rounded-full" style={{ width: '56px', height: '56px' }}><img src={USDT} width="56px" height="56px" alt="USDT" className="rounded-full w-[56px] h-[56px]" /></div>
                                                <div className="rounded-full" style={{ width: '56px', height: '56px' }}><img src="/images/apecoin.png" width="56px" height="56px" alt="WETH" className="rounded-full w-[56px] h-[56px]" /></div>
                                            </div>
                                            <div className="w-[8.75rem]">
                                                <div className="text-lg leading-10 ">USDT / WETH</div>
                                            </div>
                                        </div>
                                        <div className="flex my-[0.6rem] md:my-0 bg-[#222] rounded-2 md:rounded-none md:bg-transparent p-2 md:p-0">
                                            <div className="w-[8.25rem] md:w-[10.25rem]">
                                                <div className="text-[#7F7F7F] text-sm">Total Value Locked</div>
                                                <div className="text-[14px] sm:text-[1.15rem] mt-1 sm:mt-2">$1758790</div>
                                            </div>
                                            <div className="w-[8.25rem] md:w-[10.25rem]">
                                                <div className="text-[#7F7F7F] text-sm">Total 24h Volume</div>
                                                <div className="text-[14px] sm:text-[1.15rem] mt-1 sm:mt-2">-</div>
                                            </div>
                                        </div>
                                        <div className="md:ml-auto">
                                            <button className="flex justify-center text-black w-full mx-auto rounded-[10px] text-sm font-[600] shadow-basic-button">
                                                <div className="w-full py-[1.375rem] rounded-[10px] bg-[#0345ca] text-white hover:bg-[#82bce2]  font-medium !shadow-none  flex items-center justify-center !py-1 w-full md:w-[7.25rem] h-[4rem] !text-sm md:h-fit leading-[1.5rem]">
                                                    <span>Deposit</span>
                                                </div>
                                            </button>
                                            <div className="h-2"></div>
                                            <button className="flex justify-center text-black w-full mx-auto rounded-[10px] text-sm font-[600] shadow-basic-button">
                                                <div className="w-full py-[1.375rem] rounded-[10px] bg-[#0345ca] text-white hover:bg-[#82bce2]  font-medium flex items-center justify-center w-full md:w-[7.25rem] h-[4rem] md:h-fit !shadow-none !bg-[#333] border-[1px] !py-1 !text-sm leading-[1.5rem] border-white !text-[#fff]">
                                                    <span>Withdraw</span>
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                </div> */}
                                {
                                    allV2PairsWithLiquidity?.length > 0 ? (
                                        <>
                                            {allV2PairsWithLiquidity.map(v2Pair => (
                                                <FullPositionCard key={v2Pair.liquidityToken.address} pair={v2Pair} />
                                            ))}
                                        </>
                                    ) : (
                                        <EmptyProposals>
                                            <TYPE.body color={theme.text3} textAlign="center">
                                                No liquidity found.
                                            </TYPE.body>
                                        </EmptyProposals>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <CreatePoolModal
                    isOpen={createModalOpen}
                    onDismiss={handleDismissCreate}
                    isCreate={true}
                />

                <div className="">
                    <Footer />
                </div>
            </div>
        </>
    )
}
